<template>
    <div class="sponsors-showcase" v-if="groupedSponsors.length > 0">
        <template v-if="isVisible">
            <div
                v-for="group in groupedSponsors"
                :key="group.name || '_ungrouped'"
                class="sponsor-tier"
            >
                <div v-if="group.name && !group.hideLabel" class="tier-label">
                    <span class="tier-line"></span>
                    <h3 class="tier-title">{{ group.name }}</h3>
                    <span class="tier-line"></span>
                </div>
                <div v-else-if="!group.name && groupedSponsors.length > 1" class="tier-label">
                    <span class="tier-line"></span>
                </div>
                <div class="sponsor-grid" :style="{ '--items-per-row': group.itemsPerRow || 4 }">
                    <div
                        v-for="occasionSponsor in group.sponsors"
                        :key="occasionSponsor.sponsor_id"
                        class="sponsor-col"
                    >
                        <div class="sponsor-card">
                            <a
                                v-if="occasionSponsor.sponsor.website"
                                :href="occasionSponsor.sponsor.website"
                                target="_blank"
                                rel="noopener"
                                class="sponsor-link"
                            >
                                <div class="sponsor-image-wrapper">
                                    <img
                                        :src="occasionSponsor.sponsor.image_public_path"
                                        :alt="occasionSponsor.sponsor.name"
                                        class="sponsor-img"
                                        @error="$event.target.closest('.sponsor-card').style.display = 'none'"
                                    />
                                </div>
                            </a>
                            <div v-else class="sponsor-image-wrapper">
                                <img
                                    :src="occasionSponsor.sponsor.image_public_path"
                                    :alt="occasionSponsor.sponsor.name"
                                    class="sponsor-img"
                                    @error="$event.target.closest('.sponsor-card').style.display = 'none'"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </template>
    </div>
</template>

<script>
export default {
    name: 'LazySponsorShowcase',

    props: {
        sponsors: {
            type: Array,
            required: true,
        },
    },

    data() {
        return {
            isVisible: false,
            observer: null,
        };
    },

    computed: {
        groupedSponsors() {
            const groups = {};

            this.sponsors.forEach((occasionSponsor) => {
                if (!occasionSponsor.sponsor || !occasionSponsor.sponsor.image_public_path) {
                    return;
                }

                const sponsorGroup = occasionSponsor.sponsor_group || null;
                const groupName = sponsorGroup ? sponsorGroup.name : null;
                const configurations = sponsorGroup ? (sponsorGroup.configurations || null) : null;
                const key = groupName || '_ungrouped';

                if (!groups[key]) {
                    groups[key] = {
                        name: groupName,
                        hideLabel: configurations ? configurations.hide_label === true : false,
                        itemsPerRow: configurations ? (configurations.items_per_row || null) : null,
                        sponsors: [],
                    };
                }
                groups[key].sponsors.push(occasionSponsor);
            });

            const named = Object.values(groups).filter(g => g.name !== null);
            const ungrouped = groups['_ungrouped'] ? [groups['_ungrouped']] : [];

            return [...named, ...ungrouped];
        },
    },

    mounted() {
        this.setupIntersectionObserver();
    },

    beforeDestroy() {
        if (this.observer) {
            this.observer.disconnect();
        }
    },

    methods: {
        setupIntersectionObserver() {
            if (!('IntersectionObserver' in window)) {
                this.isVisible = true;
                return;
            }

            this.observer = new IntersectionObserver((entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        this.isVisible = true;
                        this.observer.disconnect();
                    }
                });
            }, {
                root: null,
                rootMargin: '100px',
                threshold: 0.1,
            });

            this.observer.observe(this.$el);
        },
    },
};
</script>

<style scoped>
.sponsors-showcase {
    margin: 60px 0;
}

.sponsor-tier {
    margin-bottom: 50px;
}

.tier-label {
    display: flex;
    align-items: center;
    margin-bottom: 35px;
    gap: 20px;
}

.tier-line {
    flex: 1;
    height: 2px;
    background: linear-gradient(to right, transparent, #e2e8f0, transparent);
}

.tier-title {
    margin: 0;
    font-size: 22px;
    font-weight: 700;
    color: #1a202c;
    text-transform: uppercase;
    letter-spacing: 2px;
    white-space: nowrap;
}

.sponsor-grid {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
}

.sponsor-col {
    width: calc(100% / var(--items-per-row, 4));
    display: flex;
    justify-content: center;
}

.sponsor-card {
    width: 100%;
    max-width: 170px;
    padding: 15px;
    transition: opacity 0.2s ease;
}

.sponsor-card:hover {
    opacity: 0.7;
}

.sponsor-link {
    display: block;
}

.sponsor-image-wrapper {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 150px;
}

.sponsor-img {
    max-width: 150px;
    max-height: 150px;
    margin: 10px;
    width: auto;
    height: auto;
    object-fit: contain;
}

@media (max-width: 768px) {
    .sponsors-showcase {
        margin: 40px 0;
    }

    .sponsor-tier {
        margin-bottom: 40px;
    }

    .tier-title {
        font-size: 16px;
        letter-spacing: 1.5px;
    }

    .tier-label {
        gap: 15px;
        margin-bottom: 25px;
    }

    .sponsor-col {
        width: 50%;
    }

    .sponsor-card {
        max-width: 180px;
    }

    .sponsor-image-wrapper {
        min-height: 100px;
    }

    .sponsor-img {
        max-width: 120px;
        max-height: 120px;
    }
}
</style>



// WEBPACK FOOTER //
// src/Assets/components/Sponsors/LazySponsorShowcase.vue